import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Heading, Container, PageSection, Card } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { stonePalette, fititBrandRamp } from '../theme/brand';

// Ne Google Maps — OpenStreetMap plytelės per react-leaflet/leaflet (žr. 18 punktų
// sąrašo #7). Prekės ženklo spalvos "smeigtukas" per L.divIcon (SVG, inline), ne
// numatytasis Leaflet PNG žymeklis — taip nereikia atskirai konfigūruoti webpack
// tam, kad matytųsi marker-icon.png/marker-shadow.png (dažna CRA+Leaflet spąstas).
function pinIcon(highlight) {
  const fill = highlight ? tokens.colorBrandBackground : fititBrandRamp[40];
  return L.divIcon({
    className: '',
    html: `<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="${fill}" stroke="#fff" stroke-width="1.5"/>
      <circle cx="15" cy="15" r="5.5" fill="#fff"/>
    </svg>`,
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -36],
  });
}

// Tooltip (Popup) atidaromas TIK paspaudus ant žymeklio — Leaflet numatytoji
// elgsena (žr. 20 punktų sąrašo #13; anksčiau visi būdavo priverstinai atidaryti
// iš karto per markerRef.openPopup(), kas užgriozdindavo žemėlapį).
function SalonMarker({ salon }) {
  return (
    <Marker position={[salon.lat, salon.lng]} icon={pinIcon(salon.highlight)}>
      <Popup>
        <strong>{salon.name}</strong> ({salon.city})<br />
        {salon.address}<br />
        {salon.hours}
      </Popup>
    </Marker>
  );
}

const MapIcon = iconMap.map;
const LocationIcon = iconMap.location_on;
const ScheduleIcon = iconMap.schedule;
const LayersIcon = iconMap.layers;
const LocationCityIcon = iconMap.location_city;
const InfoIcon = iconMap.info;
const PaletteIcon = iconMap.palette;
const StoreIcon = iconMap.store;
const TouchAppIcon = iconMap.touch_app;
const SupportIcon = iconMap.support_agent;

const CITIES = [
  {
    city: 'Vilnius',
    salons: [
      {
        name: 'Fitit',
        descriptionKey: 'ekspozicija.vilnius_fitit_desc',
        address: 'Eitminų g. 20, Vilnius',
        mapUrl: 'https://www.google.com/maps/place/Fitit/@54.7392005,25.2163167,17z/data=!3m1!4b1!4m5!3m4!1s0x46dd9130a8c7ec7d:0x8e2eeb4f8a65553e!8m2!3d54.7392005!4d25.2185107',
        lat: 54.7392005, lng: 25.2185107,
        hours: 'I–VII: 07:30–22:30',
        plates: 'Egger, Cleaf, Gruppo Mauro Saviola, Termopal, Kronospan, dažytas MDF.',
        highlight: true,
      },
      {
        name: 'Termopalas',
        address: 'P. Lukšio g. 32, Vilnius',
        mapUrl: 'https://www.google.com/maps/place/Termopalas/@54.7111876,25.2930051,17z/data=!4m6!3m5!1s0x46dd94e69d419933:0xf540e850ca611195!8m2!3d54.7111845!4d25.29558!16s%2Fg%2F1v299pxf?entry=ttu',
        lat: 54.7111845, lng: 25.29558,
        hours: 'I–V: 10:00–19:00 | VI: 10:00–16:00 | VII: Nedirba',
        plates: 'Cleaf, Gruppo Mauro Saviola, Termopal, Kronospan, dažytas MDF.',
      },
      {
        name: 'Impeka',
        address: 'Vytenio g. 48, Vilnius',
        mapUrl: 'https://www.google.com/maps/place/Impekahome,+salonas+Vilniuje/@54.6570372,25.220172,12.54z/data=!4m7!3m6!1s0x46dd959ee6d14d25:0xd9ed5c07aa2d74d8!8m2!3d54.6711515!4d25.2667256!15sCgZpbXBla2FaCCIGaW1wZWthkgEVZnVybml0dXJlX2FjY2Vzc29yaWVz4AEA!16s%2Fg%2F11nh4hkyz6?entry=tts',
        lat: 54.6711515, lng: 25.2667256,
        hours: 'I–V: 09:00–18:00 | VI–VII: Nedirba',
        plates: 'Egger.',
      },
    ],
  },
  {
    city: 'Kaunas',
    salons: [
      {
        name: 'Termopalas',
        address: 'Ateities pl. 34B, Kaunas',
        mapUrl: 'https://www.google.com/maps/place/Termopalas/@54.9100077,23.4249956,10z/data=!4m7!3m6!1s0x46e7220e526e5937:0xccc19c8ebd58ac2a!8m2!3d54.9100077!4d24.0347368!15sCgp0ZXJtb3BhbGFzIgOIAQFaDCIKdGVybW9wYWxhc5IBBXN0b3Jl4AEA!16s%2Fg%2F1v3klrsf?entry=tts',
        lat: 54.9100077, lng: 24.0347368,
        hours: 'I–V: 08:30–17:00 | VI: 10:00–15:00 | VII: Nedirba',
        plates: 'Cleaf, Gruppo Mauro Saviola, Termopal, Kronospan, dažytas MDF.',
      },
      {
        name: 'Impeka',
        address: 'Kauno g. 22, Ramučiai, Kaunas',
        mapUrl: 'https://www.google.com/maps/place/Kauno+g.+22,+54467+Ramu%C4%8Diai/@54.9477829,24.016078,16z/data=!4m6!3m5!1s0x46e719bd73e5efa7:0xe64c3ef1ec4d9486!8m2!3d54.9487601!4d24.0194808!16s%2Fg%2F11vk48q09n?entry=ttu',
        lat: 54.9487601, lng: 24.0194808,
        hours: 'I–V: 09:00–18:00 | VI–VII: Nedirba',
        plates: 'Egger.',
      },
    ],
  },
  {
    city: 'Klaipėda',
    salons: [
      {
        name: 'Termopalas',
        address: 'Baltijos pr. 26B, Klaipėda',
        mapUrl: 'https://www.google.com/maps/place/Termopalas/@55.68615,21.151754,16z/data=!4m6!3m5!1s0x46e4dea2b2c65d49:0x25c8358d230bae98!8m2!3d55.6856374!4d21.1528982!16s%2Fg%2F11b6lmkc6j?entry=ttu',
        lat: 55.6856374, lng: 21.1528982,
        hours: 'I–V: 08:30–17:00 | VI–VII: Nedirba',
        plates: 'Cleaf, Gruppo Mauro Saviola, Termopal, Kronospan, dažytas MDF.',
      },
      {
        name: 'Impeka',
        address: 'Birutės g. 22-315, Klaipėda',
        mapUrl: 'https://www.google.com/maps/place/Birut%C4%97s+g.+22,+91187+Klaip%C4%97da/@55.694833,21.140865,17z/data=!3m1!4b1!4m10!1m2!2m1!1sBirut%C4%97s+g.+22-315,+Klaip%C4%97da+impeka!3m6!1s0x46e4dc0b4a98c8cb:0xfab841621483df6b!8m2!3d55.6948301!4d21.1457359',
        lat: 55.6948301, lng: 21.1457359,
        hours: 'I–V: 08:00–17:00 | VI–VII: Nedirba',
        plates: 'Egger.',
      },
    ],
  },
];

// Plokščias sąrašas žemėlapio žymekliams — su miesto pavadinimu prie kiekvieno
// salono (CITIES struktūra grupuoja pagal miestą, žemėlapiui reikia plokščio sąrašo).
const SALON_MARKERS = CITIES.flatMap(({ city, salons }) => salons.map(salon => ({ ...salon, city })));
const LITHUANIA_CENTER = [55.25, 23.9];

const useStyles = makeStyles({
  root: { backgroundColor: stonePalette[50] },

  // Standartinis 2 stulpelių hero — tas pats paternas, kaip /meistro-uzsakymas
  // (Carpenter.jsx), tik dešinėje vietoj nuotraukos yra interaktyvus žemėlapis
  // (žr. 20 punktų sąrašo #13).
  // Balta (ne paveldėta stonePalette[50]) — žr. #8: sekcija su nuotrauka/žemėlapiu
  // turi būti balta visuose šios struktūros puslapiuose.
  hero: {
    backgroundColor: tokens.colorNeutralBackground1,
    paddingTop: tokens.spacingVerticalXXXL, paddingBottom: tokens.spacingVerticalXXXL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Paddingas ant heroInner (ne hero) — kai maxWidth-centravimas IR paddingas
  // yra ant SKIRTINGŲ elementų, susidaro dvigubas centravimas, kuris duoda
  // kitokį kairįjį atitraukimą nei header'io. Sudėjus abu ant to paties
  // elemento, formulė identiška TopBar.jsx/MainMenu.jsx `container` (žr.
  // /admin/bugs "Header logotipas ir language icon turi lygiuoti su
  // puslapio turiniu").
  heroInner: {
    maxWidth: '80rem', marginLeft: 'auto', marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL, paddingRight: tokens.spacingHorizontalXXL,
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalXXL, alignItems: 'center',
    '@media (min-width: 900px)': { gridTemplateColumns: '1fr 1fr' },
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  heroEyebrow: { marginBottom: tokens.spacingVerticalS },
  title: { marginBottom: tokens.spacingVerticalM },
  heroText: { fontSize: tokens.fontSizeBase400, color: tokens.colorNeutralForeground2, lineHeight: '1.6', marginBottom: tokens.spacingVerticalL, maxWidth: '32rem' },
  heroPoints: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heroPoint: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground1 },
  heroPointIcon: { color: tokens.colorBrandForeground2, fontSize: '18px', flexShrink: 0 },
  mapWrap: {
    borderRadius: tokens.borderRadiusXLarge, overflow: 'hidden', boxShadow: tokens.shadow16,
    aspectRatio: '4 / 3',
    // Leaflet vidiniai pane'ai turi savo z-index (iki 1000, žr. leaflet.css),
    // kurie be konteinerio "pabėga" virš Header.jsx z-index:50 stacking
    // context'o (tas pats paternas kaip konfigūratoriaus 3D <Html> — žr.
    // Layout.jsx isolation:'isolate'). isolation sulaiko juos vietoje, bet
    // pats mapWrap lieka normaliame dokumento sraute (be z-index), tad
    // nekyla virš Header'io meniu (įsk. atsidariusio "Informacija" submeniu).
    isolation: 'isolate',
  },

  salonsSection: { backgroundColor: tokens.colorNeutralBackground2 },
  citiesWrap: { display: 'flex', flexDirection: 'column', gap: '3.5rem' },
  cityHeader: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalXL },
  cityIcon: { fontSize: '22px', color: tokens.colorBrandForeground2 },
  cityName: { fontSize: tokens.fontSizeBase500, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  salonsGrid: {
    display: 'grid', gridTemplateColumns: '1fr', gap: tokens.spacingHorizontalL,
    '@media (min-width: 768px)': { gridTemplateColumns: '1fr 1fr 1fr' },
  },

  salonHeadRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalS },
  salonName: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase400 },
  salonBadge: {
    display: 'inline-block', marginTop: tokens.spacingVerticalXXS, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium,
    color: tokens.colorBrandForeground2, backgroundColor: tokens.colorBrandBackground2, border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusCircular, paddingLeft: tokens.spacingHorizontalS, paddingRight: tokens.spacingHorizontalS, paddingTop: tokens.spacingVerticalXXS, paddingBottom: tokens.spacingVerticalXXS,
  },
  // Standartinis Fluent <Button shape="circular"> (dydis/kampai — iš paties komponento),
  // tik fonas/kraštinė perrašomi į švelnų prekės ženklo atspalvį.
  mapBtn: {
    flexShrink: 0, backgroundColor: tokens.colorBrandBackground2, border: `1px solid ${tokens.colorBrandStroke2}`,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover },
  },

  salonDesc: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, marginBottom: tokens.spacingVerticalL, lineHeight: '1.5' },

  salonInfoList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase300 },
  salonInfoRow: { display: 'flex', alignItems: 'flex-start', gap: tokens.spacingHorizontalS },
  salonInfoIcon: { fontSize: '16px', color: tokens.colorNeutralForeground3, marginTop: '2px', flexShrink: 0 },
  salonAddressLink: { color: tokens.colorBrandForeground2, textDecorationLine: 'none', ':hover': { textDecorationLine: 'underline' } },
  salonInfoText: { color: tokens.colorNeutralForeground2 },

  infoSection: { backgroundColor: tokens.colorNeutralBackground1 },
  infoCard: { maxWidth: '48rem', marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' },
  infoIconChip: {
    width: '48px', height: '48px', backgroundColor: tokens.colorBrandBackground2, borderRadius: tokens.borderRadius3XLarge,
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto', marginRight: 'auto',
    marginBottom: tokens.spacingVerticalM, color: tokens.colorBrandForeground2, fontSize: '24px',
  },
  infoTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, marginBottom: tokens.spacingVerticalS },
  infoText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground2, marginBottom: tokens.spacingVerticalL },
});

function SalonCard({ salon, s }) {
  const { t } = useTranslation();
  return (
    <Card>
      <div className={s.salonHeadRow}>
        <div>
          <div className={s.salonName}>{salon.name}</div>
          {salon.highlight && (
            <span className={s.salonBadge}>
              {t('ekspozicija.fitit_badge')}
            </span>
          )}
        </div>
        <Button
          as="a"
          href={salon.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          appearance="outline"
          shape="circular"
          icon={<MapIcon />}
          className={s.mapBtn}
          title={t('ekspozicija.map_label')}
          aria-label={t('ekspozicija.map_label')}
        />
      </div>

      {salon.descriptionKey && <p className={s.salonDesc}>{t(salon.descriptionKey)}</p>}

      <div className={s.salonInfoList}>
        <div className={s.salonInfoRow}>
          <LocationIcon className={s.salonInfoIcon} />
          <a href={salon.mapUrl} target="_blank" rel="noopener noreferrer" className={s.salonAddressLink}>
            {salon.address}
          </a>
        </div>
        <div className={s.salonInfoRow}>
          <ScheduleIcon className={s.salonInfoIcon} />
          <span className={s.salonInfoText}>{salon.hours}</span>
        </div>
        <div className={s.salonInfoRow}>
          <LayersIcon className={s.salonInfoIcon} />
          <span className={s.salonInfoText}><em>{t('ekspozicija.plates_label')} {salon.plates}</em></span>
        </div>
      </div>
    </Card>
  );
}

function Ekspozicija() {
  const { t } = useTranslation();
  const s = useStyles();
  useEffect(() => { document.title = `${t('ekspozicija.title')} | Fitit`; }, [t]);

  return (
    <div className={s.root}>

      {/* Hero — standartinis 2 stulpelių paternas kaip /meistro-uzsakymas,
          dešinėje interaktyvus žemėlapis vietoj nuotraukos. */}
      <div className={s.hero}>
        <div className={s.heroInner}>
          <div>
            <Heading as="h1" variant="h1" className={s.title}>{t('ekspozicija.title')}</Heading>
            <p className={s.heroText}>{t('ekspozicija.subtitle')}</p>
            <div className={s.heroPoints}>
              <div className={s.heroPoint}><StoreIcon className={s.heroPointIcon} />{t('ekspozicija.hero_point_0')}</div>
              <div className={s.heroPoint}><TouchAppIcon className={s.heroPointIcon} />{t('ekspozicija.hero_point_1')}</div>
              <div className={s.heroPoint}><SupportIcon className={s.heroPointIcon} />{t('ekspozicija.hero_point_2')}</div>
            </div>
          </div>
          <div className={s.mapWrap}>
            <MapContainer center={LITHUANIA_CENTER} zoom={7} style={{ width: '100%', height: '100%' }}>
              <TileLayer
                attribution={`&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> ${t('ekspozicija.osm_contributors')}`}
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {SALON_MARKERS.map(salon => (
                <SalonMarker key={salon.name + salon.address} salon={salon} />
              ))}
            </MapContainer>
          </div>
        </div>
      </div>

      {/* Salons */}
      <PageSection className={s.salonsSection}>
        <Container size="lg" className={s.citiesWrap}>
          {CITIES.map(({ city, salons }) => (
            <div key={city}>
              <div className={s.cityHeader}>
                <LocationCityIcon className={s.cityIcon} />
                <span className={s.cityName}>{city}</span>
              </div>
              <div className={s.salonsGrid}>
                {salons.map(salon => (
                  <SalonCard key={salon.name + salon.address} salon={salon} s={s} />
                ))}
              </div>
            </div>
          ))}
        </Container>
      </PageSection>

      {/* Info box */}
      <PageSection small className={s.infoSection}>
        <Card className={s.infoCard} padding={tokens.spacingHorizontalXXXL}>
          <div className={s.infoIconChip}>
            <InfoIcon />
          </div>
          <Heading variant="h3" as="h3" className={s.infoTitle}>{t('ekspozicija.info_title')}</Heading>
          <p className={s.infoText}>{t('ekspozicija.info_text')}</p>
          <Button appearance="primary" size="large" as={Link} to="/issirinkt-plokstes" icon={<PaletteIcon fontSize={17} />}>
            {t('ekspozicija.info_btn')}
          </Button>
        </Card>
      </PageSection>

    </div>
  );
}

export default Ekspozicija;
